/**
 * MemberCareCheatGuide — how-to for the Member Care Engine (MCE) panel.
 *
 * MCE is the "Zoho draft generator" — every night it scans members, picks
 * those needing a touch (anniversary approaching, complaint, no contact in
 * 90 days), and creates a personalised draft email in Zoho Desk for the LSM
 * to review + send.
 */
import React from 'react';
import {
  Heart, Clock, Sparkles, Send, Eye, AlertTriangle, Lock, RefreshCw, FileText,
} from 'lucide-react';
import CheatGuideShell, {
  Section, Btn, Pill, Warn, Note,
} from './CheatGuideShell';

export default function MemberCareCheatGuide() {
  return (
    <CheatGuideShell
      title="Cheat Guide — Member Care Engine (the daily Zoho draft generator)"
      storageKey="sb_mce_cheat_open"
      testId="mce-cheat-guide">

      <Section title="What does it do?" icon={<Heart size={13} />}>
        Every night at <strong>03:00 IST</strong>, the Member Care Engine scans
        every member in this client and asks: "does this person need a touch
        right now?". For everyone who passes the rules, it creates a{' '}
        <strong>draft in the Member Care Workbench</strong> — already
        personalised with their Soul Form data, their LSM, the right
        WhatsApp number, and (for dormant members) a Suggested anchor
        from Claude.{' '}
        <strong>Drafts no longer auto-push to Zoho.</strong> You review them
        in the Workbench every morning, edit any line that doesn't land,
        and click Push to Zoho on the ones you approve. Time-sensitive
        birthdays/anniversaries auto-push at 18:00 IST as a safety net
        if you didn't get to them.
      </Section>

      <Section title="The rules that trigger a draft" icon={<Sparkles size={13} />}>
        <ul className="space-y-1 list-disc ml-5">
          <li><strong>Birthday today / spouse / child birthday</strong> — concierge offer</li>
          <li><strong>Wedding anniversary</strong> — celebration ideas drafted</li>
          <li><strong>Festival greeting</strong> — Diwali / Holi / regional</li>
          <li><strong>Dormant 60 / 90 days</strong> — gentle re-engagement (with Suggest button)</li>
          <li><strong>Soul Form invite</strong> — new members, follow-ups at 14d & 30d</li>
          <li><strong>Power user · still no Soul Form</strong> — VIP nudge</li>
          <li><strong>Monthly curated note · Quarterly check-in</strong> — keepsake touchpoints</li>
        </ul>
        <Note color="#FFFFFFAA">Each member can only get ONE draft per run, even if they
          match multiple rules — we pick the highest priority one.
          30-day cooldown after any MCE outreach prevents over-messaging.</Note>
      </Section>

      <Section title="The Workbench — your daily review" icon={<Send size={13} />}>
        The Member Care Workbench is where you review and approve every
        draft before it reaches Zoho. <strong>Top nav → Workbench</strong>.
        <table className="w-full text-[12px] border-collapse mt-2">
          <tbody>
            <Row icon={<Eye size={11} />} btn="Client filter chips"
                 desc="All · JLR · AABE — switch context with one click. New clients appear automatically when onboarded." />
            <Row icon={<Send size={11} />} btn="✨ Generate today's drafts (CEO)"
                 desc="Run the MCE sweep right now instead of waiting for tomorrow's 3am cron. Useful for testing or catching up." recommended />
            <Row icon={<FileText size={11} />} btn="CSV"
                 desc="Download current view as CSV — 24 columns including subject, body, soul prefs, suggested anchor, status. UTF-8 BOM for Excel." />
            <Row icon={<Eye size={11} />} btn="Preview"
                 desc="Open the full draft drawer. Click body to Edit inline → Save. If your edit was worse, click Reset to original." />
            <Row icon={<Sparkles size={11} />} btn="✨ Suggest (dormant only)"
                 desc="Claude reads the member's last 5 inbound tickets + Soul Form and surfaces ONE concrete outreach anchor. Cached after first call." />
            <Row icon={<RefreshCw size={11} />} btn="✨ Regenerate with Claude"
                 desc="Below every draft body. Re-runs Claude on this one draft — useful if the current note doesn't land, or for legacy drafts where the body never saved (rendered as 'No body')." />
            <Row icon={<Send size={11} />} btn="Send to Zoho"
                 desc="Pushes this draft to Zoho Desk as a real ticket. From this moment, Zoho is source of truth — no more edits in the Workbench." recommended />
            <Row icon={<AlertTriangle size={11} />} btn="Skip with reason"
                 desc="Drop with one of: Just spoke / Member unavailable / Member complained / Not relevant / Wrong info / Other. Other requires a free-text note." />
            <Row icon={<Send size={11} />} btn="Push selected to Zoho (bulk)"
                 desc="Confirmation modal with breakdown by trigger. Capped at 200 per call." />
          </tbody>
        </table>
      </Section>

      <Section title="What you'll see in a draft" icon={<FileText size={13} />}>
        <ul className="space-y-1 list-disc ml-5">
          <li>Subject line tagged with the trigger reason ("Anniversary planning — Mr & Mrs Mehta")</li>
          <li>Body in your LSM's voice with their signature + WhatsApp number</li>
          <li>2-3 personalisation chips ("we noticed you love Italian dining…", "your last trip to Goa…")</li>
          <li>One concrete next action ("shall I hold a sea-facing villa for the weekend of…?")</li>
        </ul>
      </Section>

      <Section title="What the run-history table tells you" icon={<Clock size={13} />}>
        <ul className="space-y-1 list-disc ml-5">
          <li><strong>Status</strong>: <Pill bg="#DCFCE7" fg="#166534">success</Pill>{' '}
            <Pill bg="#FEF3C7" fg="#92400E">partial</Pill>{' '}
            <Pill bg="#FEE2E2" fg="#7a1f1f">failed</Pill>
          </li>
          <li><strong>Drafts created</strong>: how many tickets landed in Zoho Desk</li>
          <li><strong>Skipped</strong>: members who matched a rule but were excluded (already had a draft this week, opted out, etc.)</li>
          <li><strong>Trigger</strong>: <Pill bg="#F7F5F2" fg="#1B2A4A">cron</Pill> = scheduled,
            <Pill bg="#F7F5F2" fg="#1B2A4A">manual</Pill> = someone clicked Run now</li>
        </ul>
      </Section>

      <Section title="Doctrine · We draft, we never auto-send" icon={<Lock size={13} />}>
        <strong>Every member-facing message passes through a human at the
        Workbench. This is non-negotiable.</strong> The Engine drafts. A
        person reads. A person decides. A person clicks Push. Service this
        personal cannot be automated — it can only be remembered,
        anticipated, and delivered by people who know.
        <Note color="#FFFFFFAA" style={{ marginTop: 8 }}>
          The two windows that matter:
          <ul className="space-y-1 list-disc ml-5 mt-1">
            <li><strong>T-7 prep brief</strong> — 03:00 IST. Internal LSM-facing only. "Mira's grandson turns 5 in seven days. Last birthday she ordered the chocolate marble." Gives the LSM time to prepare.</li>
            <li><strong>T-0 greeting</strong> — 08:00 IST. Member-facing draft, ready in the Workbench. LSM reviews + clicks Push.</li>
            <li><strong>18:00 IST safety net</strong> — time-sensitive birthdays / anniversaries that didn't get pushed manually go out automatically. Last resort, not the rule.</li>
          </ul>
        </Note>
      </Section>

      <Section title="Recent updates · May 2026" icon={<RefreshCw size={13} />}>
        <ul className="space-y-1 list-disc ml-5 text-[13px]">
          <li><strong>Zoho draft format locked</strong> — blue box up top (the message), WhatsApp-ready text in the middle, LSM chips at the bottom. Same shape every time, every member.</li>
          <li><strong>Drafts no longer push to Zoho automatically</strong> — Workbench is the only path.</li>
          <li><strong>"Fix now · regenerate N"</strong> button heals empty-body drafts at scale.</li>
        </ul>
      </Section>

      <Section title="When something goes wrong" icon={<AlertTriangle size={13} />}>
        <Warn>
          If the table shows a "failed" run, the most common causes are:
          (a) Zoho OAuth token expired — refresh in Settings,
          (b) Claude rate-limited — try again in 10 minutes,
          (c) member has no LSM assigned — fix in Zoho CRM.
        </Warn>
        <Note color="#FFFFFFAA" style={{ marginTop: 8 }}>
          <strong>Drafts showing "No body"?</strong> An amber banner at
          the top of the Workbench will detect them automatically. Click{' '}
          <strong>Fix now · regenerate N</strong> — Claude re-runs on
          up to 200 drafts at a time. Click again if any remain. New
          drafts (from tonight's 03:00 IST cron onwards) will always
          have proper bodies.
        </Note>
      </Section>

      <Section title="Permissions" icon={<Lock size={13} />}>
        <Pill bg="#DBEAFE" fg="#1E3A8A">CEO</Pill> can run dry-run + run-now.
        <Pill bg="#DCFCE7" fg="#166534"> Leaders</Pill> can dry-run only —
        Run-now is locked to prevent accidental Zoho draft floods.
      </Section>

      <Note color="#FFFFFFAA">
        <strong style={{ color: '#7A5C2B' }}>Pro tip:</strong> Drafts sit in Zoho Desk
        with the category <code>Member Care Engine</code>. Filter on that category to see
        the day's batch. LSMs can bulk-review and send.
      </Note>
    </CheatGuideShell>
  );
}

function Row({ icon, btn, desc, recommended }) {
  return (
    <tr style={{ borderTop: '1px solid #E2D8C2' }}>
      <td className="py-1.5 pr-3 align-top whitespace-nowrap">
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border align-middle text-[11px]"
          style={{ borderColor: '#E5E1DC', background: '#FFFFFF', color: '#1B2A4A' }}>
          {icon} {btn}
        </span>
        {recommended && <span style={{ color: '#7A5C2B', marginLeft: 4 }}>★</span>}
      </td>
      <td className="py-1.5 align-top" style={{ color: '#3D3528' }}>{desc}</td>
    </tr>
  );
}
